<template>
    <div class="tw:bg-white tw:rounded-xl">
        <BaseTable
            :columns="columns"
            :data="bases"
            :skeleton="loading"
            :skeleton-rows="5"
            empty-text="No hay deportistas disponibles"
            :default-col-width="defaultColWidth"
        >
            <!-- Skeleton headers -->
            <template #skeleton-header-activo>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-4 tw:mx-auto"></div>
            </template>
            <template #skeleton-header-nombre>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-apellidos>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-header-dni>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-email>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>
            <template #skeleton-header-tipo>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-10"></div>
            </template>
            <template #skeleton-header-opciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-auto tw:mx-auto"></div>
            </template>

            <!-- Skeleton rows -->
            <template #skeleton-activo>
                <div class="tw:h-4 tw:w-4 tw:bg-gray-200 tw:rounded-full tw:mx-auto"></div>
            </template>
            <template #skeleton-nombre>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-apellidos>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-dni>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-email>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-38"></div>
            </template>
            <template #skeleton-tipo>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-opciones>
                <div class="tw:h-9 tw:w-28 tw:bg-gray-200 tw:rounded tw:mx-auto"></div>
            </template>

            <template #cell-activo="{ row }">
                <div
                    :class="['tw:h-3 tw:w-3 tw:rounded-full', getActivoClass(row.activo)]"
                    :title="getActivoTitle(row.activo)"
                ></div>
            </template>

            <template #cell-tipo="{ row }">
                {{ getTipoLabel(row.metodo_pago) }}
            </template>

            <template #cell-opciones="{ row, index }">
                <OpcionesDropdownTableComponent
                    :last-index="bases.length > 3 && index >= bases.length - 3"
                    :options="getRowOptions(row)"
                />
            </template>
        </BaseTable>

        <!-- Modales inline -->
        <template v-for="item in bases">
            <EnviarBase :key="'enviar-' + item.id" :base="item" @close="closeModal" />
            <AnularBase :key="'anular-' + item.id" :base="item" @close="closeModal" @actualizado="$emit('refresh')" />
            <BorrarModalComponent
                :key="'borrar-' + item.id"
                :name="'borrar-' + item.id"
                :title="'Borrar deportista ' + item.nombre"
                @confirm="borrarDeportista(item.id)"
                @close="closeModal"
            />
        </template>
    </div>
</template>

<script setup>
    import { computed, getCurrentInstance } from 'vue'
    import store from '@/store'
    import { config as appConfig } from '@/config.js'
    import { useToast } from '@/composables/common/useToast'
    import { useRoute } from '@/composables/common/router/useRouter'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import OpcionesDropdownTableComponent from '@/modules/admin/ui/OpcionesDropdownTableComponent.vue'
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue'
    import EnviarBase from '@/modules/admin/bases/components/modals/EnviarBase.vue'
    import AnularBase from '@/modules/admin/bases/components/modals/AnularBaseModal.vue'

    const props = defineProps({
        bases: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    })

    const emit = defineEmits(['refresh'])
    const toast = useToast()
    const { proxy } = getCurrentInstance()
    const route = useRoute()

    const columns = [
        { key: 'activo', label: 'Estado', centered: true, class: 'tw:w-20'},
        { key: 'nombre', label: 'Nombre'},
        { key: 'apellidos', label: 'Apellidos', class: 'tw:w-44'},
        { key: 'dni', label: 'DNI'},
        { key: 'email', label: 'Email', class: 'tw:w-64'},
        { key: 'tipo', label: 'Tipo'},
        { key: 'opciones', label: 'Opciones', centered: true, class: 'tw:w-32'}
    ]

    function getActivoClass(activo) {
        return activo ? 'tw:bg-green-500' : 'tw:bg-red-500'
    }

    function getActivoTitle(activo) {
        return activo ? 'Activo' : 'Inactivo'
    }

    function getTipoLabel(metodoPago) {
        return (metodoPago === 5 || metodoPago === 8) ? 'ONLINE' : 'TAQUILLA'
    }

    function getRowOptions(row) {
        const options = [
            {
                to: { name: 'back.bases.show', params: { base_id: row.id, lista_id: route.params.lista_id } },
                label: 'Ver',
                icon: 'eye'
            }
        ]

        if (row.user_id) {
            options.push({
                to: {
                    name: 'back.usuarios.show-user',
                    params: { lista_id: route.params.lista_id, user_id: row.user_id }
                },
                label: 'Ver usuario',
                icon: 'user'
            })
        }

        options.push({
            to: {
                name: 'back.bases.update',
                params: { base_id: row.id, lista_id: route.params.lista_id }
            },
            label: 'Editar',
            icon: 'edit'
        })

        options.push({
            type: 'action',
            action: () => showModal('enviar-' + row.id),
            label: 'Enviar',
            icon: 'envelope'
        })

        options.push({
            type: 'action',
            action: () => descargarPDF(row.url_pdf),
            label: 'Descargar Base',
            icon: 'download'
        })

        if (row.activo) {
            options.push({
                type: 'action',
                action: () => showModal('anular-' + row.id),
                label: 'Realizar devolución',
                icon: 'undo'
            })
        } else {
            options.push({
                type: 'action',
                action: () => showModal('borrar-' + row.id),
                label: 'Borrar',
                icon: 'trash'
            })
        }

        options.push({
            type: 'url',
            url: row.url_recibo + '&tid=' + store.getters.getConfig?.hash_teatro,
            label: 'Imprimir recibo',
            icon: 'print'
        })

        return options
    }

    function showModal(modal) {
        if (proxy?.$modal) {
            proxy.$modal.show(modal)
        }
    }

    function closeModal(modal) {
        if (proxy?.$modal) {
            proxy.$modal.hide(modal)
        }
    }

    function descargarPDF(url) {
        window.open(url, '_blank')
    }

    async function borrarDeportista(baseId) {
        const params = {
            filters: {},
            lista_id: proxy?.$route?.params?.lista_id,
            base_id: baseId
        }

        try {
            const res = await store.dispatch(appConfig.getVuexAlias('back.bases.borrar'), params)
            toast.success(res.data.message)
            emit('refresh')
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al borrar el deportista')
        }
    }
</script>
